import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Mic,
  Psychology,
  ExpandMore,
  ExpandLess,
  Clear,
} from '@mui/icons-material';

interface Transcription {
  text: string;
  confidence: number;
  timestamp: number;
}

interface AudioAnalysis {
  analysis: string;
  insights: string[];
  actions: string[];
  confidence: number;
  timestamp: number;
}

interface AudioPanelProps {
  transcriptions: Transcription[];
  audioAnalysis: AudioAnalysis[];
  onClearTranscriptions: () => void;
  onClearAnalysis: () => void;
}

const AudioPanel: React.FC<AudioPanelProps> = ({
  transcriptions,
  audioAnalysis,
  onClearTranscriptions,
  onClearAnalysis,
}) => {
  const [expanded, setExpanded] = React.useState(true);
  
  // Refs for auto-scrolling
  const transcriptionsEndRef = React.useRef<HTMLDivElement>(null);
  const analysisEndRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new content is added
  React.useEffect(() => {
    if (transcriptionsEndRef.current) {
      transcriptionsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcriptions]);

  React.useEffect(() => {
    if (analysisEndRef.current) {
      analysisEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [audioAnalysis]);

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'success';
    if (confidence >= 0.6) return 'warning';
    return 'error';
  };

  return (
    <Paper
      elevation={4}
      sx={{
        width: '100%',
        maxWidth: 800,
        margin: '24px auto 0 auto',
        maxHeight: 500,
        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(30,30,30,0.7)' : 'rgba(255,255,255,0.7)',
        borderRadius: 3,
        p: 1,
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
        border: 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          p: 1.2,
          background: 'linear-gradient(90deg, rgba(25,118,210,0.06) 0%, rgba(255,255,255,0.06) 100%)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          borderTopLeftRadius: 22,
          borderTopRightRadius: 22,
          borderBottomLeftRadius: 18,
          borderBottomRightRadius: 18,
          boxShadow: '0 1px 4px 0 rgba(25,118,210,0.04)',
          mb: 1,
        }}
      >
        <Mic sx={{ mr: 1, fontSize: 20, color: 'primary.light' }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main', letterSpacing: 0.2, fontSize: 16, textShadow: 'none' }}>
          Audio Analysis
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          sx={{ ml: 1 }}
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ maxHeight: 400, overflow: 'auto', p: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Transcriptions ({transcriptions.length})
                </Typography>
                {transcriptions.length > 0 && (
                  <IconButton size="small" onClick={onClearTranscriptions}>
                    <Clear fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {transcriptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No transcriptions yet. Start speaking while mic is active.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'rgba(25,118,210,0.03)', borderRadius: 2, p: 1 }}>
                  <List dense sx={{ p: 0 }}>
                    {transcriptions.map((transcription, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">
                                "{transcription.text}"
                              </Typography>
                              <Chip
                                label={`${Math.round(transcription.confidence * 100)}%`}
                                size="small"
                                color={getConfidenceColor(transcription.confidence) as any}
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={formatTime(transcription.timestamp)}
                        />
                      </ListItem>
                    ))}
                    {/* Auto-scroll anchor for transcriptions */}
                    <div ref={transcriptionsEndRef} />
                  </List>
                </Box>
              )}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'divider' }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Psychology color="primary" fontSize="small" />
                  <Typography variant="subtitle2" color="text.secondary">
                    AI Analysis ({audioAnalysis.length})
                  </Typography>
                </Box>
                {audioAnalysis.length > 0 && (
                  <IconButton size="small" onClick={onClearAnalysis}>
                    <Clear fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {audioAnalysis.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No AI analysis yet. Speak clearly for better results.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'rgba(25,118,210,0.03)', borderRadius: 2, p: 1 }}>
                  <List dense sx={{ p: 0 }}>
                    {audioAnalysis.map((analysis, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {analysis.analysis}
                              </Typography>
                              <Chip
                                label={`${Math.round(analysis.confidence * 100)}%`}
                                size="small"
                                color={getConfidenceColor(analysis.confidence) as any}
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={formatTime(analysis.timestamp)}
                        />
                      </ListItem>
                    ))}
                    {/* Auto-scroll anchor for analysis */}
                    <div ref={analysisEndRef} />
                  </List>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AudioPanel; 